import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, classCalendarUrl, type MyClass } from '../api';

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
  });

const countdown = (iso: string) => {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return null;
  const hours = Math.round(ms / 3600_000);
  if (hours < 1) return 'starting soon';
  if (hours < 24) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `in ${days} day${days === 1 ? '' : 's'}`;
};

/** Every live class this trainee holds a place on. */
export function MyClasses() {
  const [data, setData] = useState<{ upcoming: MyClass[]; past: MyClass[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try { setData(await api.get('/courses/mine/calendar')); }
      catch (e) { setError((e as ApiError).message); }
    })();
  }, []);

  if (error) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  return (
    <>
      <h1 className="h1">My classes</h1>

      {data.upcoming.length === 0 && (
        <p className="notice">
          You have no classes booked. Live classes appear inside the course that runs them —
          open the course and look for a live session unit.
        </p>
      )}

      {data.upcoming.length > 0 && (
        <ul className="classes">
          {data.upcoming.map((c) => (
            <li key={c.sessionId} className="classes__row is-mine">
              <div className="classes__main">
                <strong>{c.name}</strong>
                <div className="muted small">
                  {when(c.startsAt)} · {c.durationMinutes} min
                  {c.location ? ` · ${c.location}` : ''}
                </div>
                <div className="muted small">
                  <Link to={`/courses/${c.courseId}/units/${c.unitId}`}>{c.courseName} — {c.unitName}</Link>
                </div>
              </div>
              <div className="row">
                <span className="pill pill--pending_review">{countdown(c.startsAt)}</span>
                {c.conferenceUrl && (
                  <a className="btn btn--primary" href={c.conferenceUrl} target="_blank" rel="noreferrer">Join</a>
                )}
                <a className="btn btn--ghost" href={classCalendarUrl(c.courseId, c.unitId, c.sessionId)}>
                  Add to calendar
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}

      {data.past.length > 0 && (
        <>
          <h2 className="h2">Classes you have been to</h2>
          <ul className="classes">
            {data.past.map((c) => (
              <li key={c.sessionId} className="classes__row is-past">
                <div className="classes__main">
                  <strong>{c.name}</strong>
                  <div className="muted small">{when(c.startsAt)} · {c.courseName}</div>
                </div>
                <span className={`pill ${c.attended === true ? 'pill--completed' : c.attended === false ? 'pill--failed' : 'pill--pending_review'}`}>
                  {c.attended === true ? 'attended' : c.attended === false ? 'marked absent' : 'register not taken yet'}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
